import { Injectable } from '@angular/core';
import { AuthService } from '@gooru/navigator-auth-widget-ui';
import { generateUUID, HttpService, SessionService, TenantSettingsModel } from '@gooru/navigator-core-widget-ui';
import { UserContextService } from './user-context.service';
import { UserService } from '@gooru/my-gooru-ts';
import { OtpService } from './otp.service';
import { INIT_ACTION_TYPE, LOGINTYPE, MYGOORU_AI_CONTEXT_TYPE, NEW_USER, USERCATEGORY } from '../../constants/helper-constant';
import { CONFIG } from '../../config';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
@Injectable({
  providedIn: 'root',
})
export class AuthServiceHelper {
  private readonly ANONYMOUS_TOKEN = 'anonymous_token';
  public isLearner!: boolean;
  public isInstructor!: boolean;
  public isAutoLoginProcessing: boolean = false;
  private profileNamespace = 'api/nucleus/v2/profiles';

  constructor(
    private authService: AuthService,
    private httpService: HttpService,
    private userContextService: UserContextService,
    private userService: UserService,
    private otpService: OtpService,
    private sessionService: SessionService,
    private router: Router,
  ) {}

  /**
   * @function signInAnonymousUsingNonce
   * This method is used to signin with nonce token
   */
  public async signInAnonymousUsingNonce(token: string): Promise<any> {
    const headers = this.getNonceHeaders(token);
    const reqOpts = { headers };
    return await this.authService.signInAnonymousNokey(reqOpts).then((res) => {
      return res;
    });
  }

  /**
   * @function autoLoginWithCookie
   * This method is used to signin with cookies
   */
  public async autoLoginWithCookie(accessToken: any) {
    const { version, clientId, clientKey, appId, defaultUserRole } = CONFIG;
    const userDetails = await this.userService?.getUserDetailsByToken(
      accessToken,
      appId
    );
    
    if (!userDetails || typeof userDetails === 'string') {
      console.error('Invalid user details');
      return;
    }

    let loginType;
    const headers = this.httpService.getHeaders(accessToken);
    const reqOpts = { headers };
    this.authService
      .fetchUserAccounts(userDetails.email || '', reqOpts)
      .then(async (userData) => {
        const user = userData.find((user) => user.tenantId === (userDetails as any).tenantId);
        loginType = user?.login_type ?? LOGINTYPE.CREDENTIAL;
      });
    this.userContextService.setContact({
      contact: userDetails.email || '',
      contactType: 'external',
      loginType: loginType,
    });
    const userContext = {
      id: (userDetails as any).user_id,
      email: userDetails.email || '',
      role: (userDetails as any).user_category,
      firstname: (userDetails as any).first_name,
      lastname: (userDetails as any).last_name,
      username: (userDetails as any).username || '',
      access_token: (userDetails as any).access_token,
    };
    const refreshToken = (userDetails as any)?.isRefreshToken;
    await this.userService.setUserSession(
      clientId,
      clientKey,
      appId,
      userContext,
      accessToken,
      refreshToken
    );
    const newUser = this.getCookie('new_user');
    const isNewUser = newUser === 'true';
    if (isNewUser) {
      const session = await this.authService.signInAnonymous(
        environment.clientId,
        environment.clientKey
      );
      sessionStorage.setItem(this.ANONYMOUS_TOKEN, session.access_token);
      localStorage.setItem(NEW_USER, 'true');
    }
    this.otpService?.loggedIn();
    const isPhone = /^\d{10,15}$/.test((userDetails as any).username || '');
    this.userContextService.setContact({
      contact: (userDetails as any).username || userDetails.email || '',
      contactType: 'external',
      first_name: (userDetails as any).first_name || 'Guest',
      role: (userDetails as any).user_category || 'student',
      user_id: (userDetails as any).user_id,
      loginType: loginType
    });
    this.router.navigate(['home']);
  }

  /**
   * @function getCookie
   * this method to get cookies
   */
  public getCookie(name: string): string | null {
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
  }
  
  /**
   * @function getNonceHeaders
   * This method is used to get the nonce headers
   */
  public getNonceHeaders(token: string) {
    return {
      'Content-Type': 'application/json',
      Authorization: `Nonce ${token}`,
    };
  }

  /**
   * @function signInWithCredential
   * This method is used to signin with credentials
   */
  public signInWithCredential(
    usernameOrEmail: string,
    password: string,
    accessToken: string | null = null
  ): Promise<any> {
    const { version, clientId, clientKey, appId } = CONFIG;

    const postData: any = {};
    postData.grant_type = LOGINTYPE.CREDENTIAL;
    postData.long_lived_access = true;
    if (accessToken) {
      postData.anonymous_token = accessToken;
    } else {
      postData.client_id = clientId;
      postData.client_key = clientKey;
    }

    return this.authService
      .signInWithCredential(
        usernameOrEmail,
        password,
        accessToken,
        clientId,
        clientKey
      )
      .then(async (payload) => {
        const hostname = window.location.hostname;
        if (hostname.startsWith('learner')) {
          this.isLearner = true;
        } else if (hostname.startsWith('instructor')) {
          this.isInstructor = true;
        }
        if ((this.isLearner && payload.user_category === USERCATEGORY.STUDENT) || this.isInstructor && payload.user_category === USERCATEGORY.TEACHER) {
          this.userContextService.setContact({
            contact: usernameOrEmail,
            contactType: 'email',
            loginType: LOGINTYPE.CREDENTIAL,
          });
          const userContext = {
            id: payload.user_id,
            email: payload.email,
            role: payload.user_category,
            firstname: payload.first_name,
            lastname: payload.last_name,
            username: payload.username || '',
            access_token: payload.access_token,
          };
          await this.userService.setUserSession(
            clientId,
            clientKey,
            appId,
            userContext,
            payload.access_token,
            payload.isRefreshToken
          );
          this.otpService.loggedIn();

          return payload;
        } else {
          return USERCATEGORY.TEACHER;
        }
      })
      .catch((error) => {
        return error;
      });
  }

  /**
   * @function accounts
   * This method is used to get the accounts for email
   */
  public async accounts(email: string) {
    const token: any = sessionStorage.getItem(this.ANONYMOUS_TOKEN);
    const headers = this.httpService.getHeaders(token);
    const reqOpts = { headers };
    return this.authService.fetchUserAccounts(email, reqOpts).then((res) => {
      return res;
    });
  }

  /**
   * @function tenantsetting
   * This method is used to get the tenant setting
   */
  public async tenantsetting(token: any) {
    const endpoint = 'api/nucleus/v1/lookups/tenant-settings';
    const headers = this.httpService.getHeaders(token);
    const reqOpts = { headers };
    return await firstValueFrom(this.httpService.get<TenantSettingsModel>(endpoint,{},reqOpts)).then((result) => {
      const tenantSettings = result ? result.tenant_settings : null;
      if (!tenantSettings) {
        return {} as TenantSettingsModel;
      }
      return tenantSettings
    });
  }

  /**
   * @function searchUser
   * This Method is used to check user existance
   */
  public async searchUser(email: any, token: any) {
    return this.authService.searchUser(email, token);
  }

  /**
   * @function resetPassword
   * This Method is used to reset the password
   */
  public async resetPassword(tenantId: any, email: any, token: any) {
    return this.authService.resetPassword(tenantId, email, token, environment.appId);
  }

  /**
   * @function resetNewPassword
   * This Method is used to set the new password
   */
  public async resetNewPassword(password: string, token: string) {
    const session = await this.authService.signInAnonymous(
      environment.clientId,
      environment.clientKey
    );
    return this.authService.resetNewPassword(password, token, session.access_token)
  }

  /**
   * @function getTenantDetails
   * This method is used to get tenant details
   */
  public async getTenantDetails(tenantId: string, token: string): Promise<any> {
    const endpoint = `api/admin/v1/tenant/${tenantId}`;
    const headers = this.httpService.getHeaders(token);
    const reqOpts = { headers };
    try {
      const result = await firstValueFrom(
        this.httpService.get<any>(endpoint, {}, reqOpts)
      );
      return result || {};
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
      return {};
    }
  }

  /**
   * @function getUserData
   * This method is used to get user data
   */
  public async getUserData(userEmail: any, token: any) {
    const encodedEmail = encodeURIComponent(userEmail);
    const endpoint = `${this.profileNamespace}/search?email=${encodedEmail}`;
    const headers = this.httpService.getHeaders(token);
    const reqOpts = { headers };
    try {
      const result = await firstValueFrom(
        this.httpService.get<any>(endpoint, {}, reqOpts)
      );
      return result || {};
    } catch (error) {
      console.error('Error fetching tenant settings:', error);
      return {};
    }
  }
}
